import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, Phone } from "lucide-react";
import { BookingApi, PaymentApi } from "../../api/services";
import { errorMessage } from "../../api/client";
import { external } from "../../platform";
import { Call911, PageHeader, PriorityBadge, SimBadge, Spinner, StatusBadge } from "../../components/common";
import { TripProgress, TripTimeline } from "../../components/TripTimeline";
import { MapView, MapMarker } from "../../maps/MapView";
import { useRoute } from "../../maps/useRoute";
import { dateTime, peso, phone, timeOnly } from "../../lib/format";

export default function BookingDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const [payBusy, setPayBusy] = useState(false);
  const { data: b, isLoading, error } = useQuery({ queryKey: ["booking", id], queryFn: () => BookingApi.get(id), refetchInterval: 30000 });
  const cancel = useMutation({
    mutationFn: () => BookingApi.cancel(id, "Cancelled by patient"),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["booking"] }); qc.invalidateQueries({ queryKey: ["bookings"] }); toast.success("Booking cancelled"); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const r = b?.responder;
  const live = b && r?.location && ["accepted", "en_route", "in_transit"].includes(b.status);
  const target = b ? (b.status === "in_transit" && b.destination ? b.destination : b.pickup) : null;
  const { route, routed, durationS, noRouting } = useRoute(live ? r.location : b?.pickup, live ? target : b?.destination);
  if (isLoading) return <Spinner />;
  if (error || !b) return <p className="text-amber-800" data-testid="booking-load-error">{errorMessage(error)}</p>;

  const markers: MapMarker[] = [{ id: "pickup", lat: b.pickup.lat, lng: b.pickup.lng, kind: "pickup", label: "Pickup" }];
  if (b.destination) markers.push({ id: "dest", lat: b.destination.lat, lng: b.destination.lng, kind: "destination", label: "Destination" });
  if (r?.location && !["completed", "cancelled"].includes(b.status)) markers.push({ id: "resp", lat: r.location.lat, lng: r.location.lng, kind: "vehicle", label: `${r.name} · ${r.vehicle?.plate || ""}` });
  const accepted = ["accepted", "en_route", "at_pickup", "in_transit", "completed"].includes(b.status);
  const eta = durationS ? Math.max(1, Math.round(durationS / 60)) : null;

  const pay = async () => {
    setPayBusy(true);
    try { const { payment } = await PaymentApi.payBooking(b.id); external.open(payment.checkout_url); }
    catch (e) { toast.error(errorMessage(e)); setPayBusy(false); }
  };

  return (
    <div className="space-y-5">
      <PageHeader title={b.type === "emergency" ? "SOS Ambulance" : b.type === "ptv" ? "PTV ride" : "Scheduled transport"} subtitle={`Reference ${b.code}`}
        action={<div className="flex items-center gap-2"><PriorityBadge p={b.priority} /><StatusBadge status={b.status} label={b.status_label} testId="booking-status-badge" /></div>} />
      {params.get("confirmed") && (
        <div className="flex gap-3 rounded-2xl border border-emerald-300 bg-emerald-50 p-4 text-emerald-900" data-testid="booking-server-confirmation">
          <CheckCircle2 className="shrink-0" />
          <p className="text-sm"><strong>Received by FASTPASS server</strong> at {timeOnly(b.created_at)} (ref {b.code}). {b.responder ? "A nearby vehicle has been matched." : "We are finding the nearest driver; dispatch has been alerted."}</p>
        </div>
      )}
      {b.type === "emergency" && !["completed", "cancelled"].includes(b.status) && <Call911 compact />}
      <div className="fp-card"><TripProgress status={b.status} /></div>
      <MapView className="h-72" markers={markers} route={route} routeDashed={!routed} testId="booking-map" />
      {route && !routed && <p className="-mt-3 text-xs text-slate-500">{noRouting ? "Dashed line is straight-line distance; road routing is not configured." : "Dashed line is approximate while the road route loads."}</p>}

      {r && accepted ? (
        <div className="fp-card space-y-3 border-[#036248] bg-gradient-to-br from-white to-[#EEF7F3]" data-testid="booking-responder-card">
          <div className="flex items-center gap-4">
            {r.photo ? <img src={r.photo} alt={r.name} className="h-16 w-16 rounded-2xl object-cover ring-2 ring-[#FFC400]" data-testid="driver-photo" />
              : <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#036248] text-xl font-bold text-white" data-testid="driver-photo-placeholder">{r.name.replace(/\[SIM\]\s*/, "").slice(0, 1)}</div>}
            <div className="min-w-0 flex-1">
              <p className="fp-label">Your driver</p>
              <p className="flex flex-wrap items-center gap-2 text-lg font-bold text-[#003F32]" data-testid="driver-name">{r.name} <SimBadge show={r.is_simulated} /></p>
              <p className="text-sm text-slate-600" data-testid="driver-vehicle">{r.vehicle ? r.vehicle.type : "Vehicle pending"}</p>
            </div>
            {r.vehicle && <span className="rounded-lg border-2 border-[#003F32] bg-[#FFC400] px-2 py-1 font-mono text-sm font-bold text-[#003F32]" data-testid="driver-plate">{r.vehicle.plate}</span>}
          </div>
          <div className="flex items-center gap-3">
            <img src="/ptv-van.png" alt="" className="h-10 w-auto" />
            <p className="flex-1 text-sm text-[#003F32]" data-testid="driver-eta">{eta && ["accepted", "en_route"].includes(b.status) ? <>Arriving in <strong>~{eta} min</strong></> : eta && b.status === "in_transit" ? <>~{eta} min to destination</> : b.status_label}{r.location?.updated_at && <span className="block text-xs text-slate-500">GPS updated {timeOnly(r.location.updated_at)}</span>}</p>
            {r.phone && <a href={`tel:${r.phone}`} className="fp-btn-primary" data-testid="booking-call-responder"><Phone size={16} /> Call {phone(r.phone)}</a>}
          </div>
        </div>
      ) : r ? (
        <div className="fp-card text-sm text-slate-600" data-testid="booking-matched-waiting">A {r.vehicle?.type || "vehicle"} has been matched. Waiting for the driver to accept — driver details appear once accepted.</div>
      ) : !["completed", "cancelled"].includes(b.status) && (
        <div className="fp-card text-sm text-slate-600" data-testid="booking-waiting-dispatch">Finding the nearest available driver… FASTPASS dispatch is also notified.</div>
      )}
      {b.fare_breakdown && (
        <div className="fp-card space-y-1 text-sm" data-testid="booking-fare-breakdown">
          <p className="fp-label mb-1">Fare estimate</p>
          <div className="flex justify-between"><span>{b.fare_breakdown.service_name} base</span><span>{peso(b.fare_breakdown.base)}</span></div>
          {b.fare_breakdown.km != null && <div className="flex justify-between"><span>Distance {b.fare_breakdown.km} km</span><span>{peso(b.fare_breakdown.distance_fee)}</span></div>}
          {b.fare_breakdown.addons.map((a: any) => <div key={a.id} className="flex justify-between"><span>{a.name}</span><span>{peso(a.fee)}</span></div>)}
          <div className="flex justify-between border-t border-slate-100 pt-1 font-bold text-[#003F32]"><span>Total</span><span>{peso(b.fare_breakdown.total)}</span></div>
        </div>
      )}

      <div className="fp-card grid gap-3 text-sm sm:grid-cols-2">
        <div><p className="fp-label">Pickup</p><p>{b.pickup.address || `${b.pickup.lat.toFixed(5)}, ${b.pickup.lng.toFixed(5)}`}</p></div>
        {b.destination && <div><p className="fp-label">Destination</p><p>{b.destination.address || "Pinned destination"}</p></div>}
        {b.scheduled_at && <div><p className="fp-label">Scheduled</p><p>{dateTime(b.scheduled_at)}</p></div>}
        <div><p className="fp-label">{b.type === "emergency" ? "Condition" : "Purpose"}</p><p>{b.condition || "—"}</p></div>
        <div><p className="fp-label">Fare estimate</p><p data-testid="booking-fare">{peso(b.fare_estimate)} · <span className="capitalize">{b.payment_status}</span></p></div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        {["requested", "assigned"].includes(b.status) && (
          <button className="fp-btn-secondary flex-1 border-amber-300 text-amber-800" disabled={cancel.isPending} onClick={() => window.confirm("Cancel this booking?") && cancel.mutate()} data-testid="booking-cancel-button">Cancel booking</button>
        )}
        {b.status === "completed" && b.payment_status !== "paid" && (
          <button className="fp-btn-primary flex-1" onClick={pay} disabled={payBusy} data-testid="booking-pay-button">Pay {peso(b.fare_estimate)} (QR Ph, GCash, Maya, card)</button>
        )}
      </div>

      <div className="fp-card"><p className="fp-label mb-3">Timeline</p><TripTimeline timeline={b.timeline} /></div>
    </div>
  );
}
